// NotificationTriggers.js
// Business logic utilities for triggering notifications

import notificationService from "./NotificationService";
import { Auth } from "aws-amplify";

class NotificationTriggers {
  // Offer-related notifications
  static async notifyOfferReceived(recipientUserId, offerDetails) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "New Offer Received",
        message: `You have received a new offer for ${offerDetails.productName} - ${offerDetails.quantity} MT at $${offerDetails.pricePerMT}/MT`,
        type: notificationService.NotificationTypes.OFFER_RECEIVED,
        metadata: JSON.stringify({
          offerId: offerDetails.id,
          productId: offerDetails.productId,
          action: "view_offer",
        }),
      });
    } catch (error) {
      console.error("Failed to send offer received notification:", error);
    }
  }

  static async notifyOfferAccepted(recipientUserId, offerDetails) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "Offer Accepted! ",
        message: `Your offer for ${offerDetails.productName} has been accepted. Total value: $${offerDetails.totalValue}`,
        type: notificationService.NotificationTypes.OFFER_ACCEPTED,
        metadata: JSON.stringify({
          offerId: offerDetails.id,
          productId: offerDetails.productId,
          action: "view_contract",
        }),
      });
    } catch (error) {
      console.error("Failed to send offer accepted notification:", error);
    }
  }

  static async notifyOfferRejected(
    recipientUserId,
    offerDetails,
    reason = null
  ) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "Offer Update",
        message: `Your offer for ${offerDetails.productName} was not accepted${
          reason ? `: ${reason}` : "."
        }`,
        type: notificationService.NotificationTypes.OFFER_REJECTED,
        metadata: JSON.stringify({
          offerId: offerDetails.id,
          productId: offerDetails.productId,
          reason: reason,
          action: "revise_offer",
        }),
      });
    } catch (error) {
      console.error("Failed to send offer rejected notification:", error);
    }
  }

  // Requirement matching notifications
  static async notifyRequirementMatched(recipientUserId, matchDetails) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "New Requirement Match Found! ",
        message: `A buyer is looking for ${matchDetails.productName} - ${matchDetails.quantity} MT. This matches your inventory!`,
        type: notificationService.NotificationTypes.REQUIREMENT_MATCHED,
        metadata: JSON.stringify({
          requirementId: matchDetails.requirementId,
          productId: matchDetails.productId,
          buyerId: matchDetails.buyerId,
          action: "create_offer",
        }),
      });
    } catch (error) {
      console.error("Failed to send requirement matched notification:", error);
    }
  }

  // Payment notifications
  static async notifyPaymentDue(recipientUserId, paymentDetails) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "Payment Due Reminder ",
        message: `Payment of $${paymentDetails.amount} is due on ${paymentDetails.dueDate} for order ${paymentDetails.orderId}`,
        type: notificationService.NotificationTypes.PAYMENT_DUE,
        metadata: JSON.stringify({
          orderId: paymentDetails.orderId,
          amount: paymentDetails.amount,
          dueDate: paymentDetails.dueDate,
          action: "make_payment",
        }),
      });
    } catch (error) {
      console.error("Failed to send payment due notification:", error);
    }
  }

  // System notifications
  static async notifySystemUpdate(recipientUserId, updateDetails) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "System Update 🔄",
        message: updateDetails.message,
        type: notificationService.NotificationTypes.SYSTEM_UPDATE,
        metadata: JSON.stringify({
          updateType: updateDetails.type,
          version: updateDetails.version,
          action: "view_details",
        }),
      });
    } catch (error) {
      console.error("Failed to send system update notification:", error);
    }
  }

  // User onboarding notifications
  static async notifyUserWelcome(recipientUserId, userDetails) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "Welcome to AMTPL!",
        message: `Welcome ${userDetails.name}! Your account has been successfully created. Start exploring agricultural opportunities.`,
        type: notificationService.NotificationTypes.SUCCESS,
        metadata: JSON.stringify({
          userType: userDetails.userType,
          registrationDate: userDetails.registrationDate,
          action: "complete_profile",
        }),
      });
    } catch (error) {
      console.error("Failed to send welcome notification:", error);
    }
  }

  // Profile completion reminder
  static async notifyProfileIncomplete(recipientUserId, missingFields) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "Complete Your Profile",
        message: `Please complete your profile to access all features. Missing: ${missingFields.join(
          ", "
        )}`,
        type: notificationService.NotificationTypes.WARNING,
        metadata: JSON.stringify({
          missingFields: missingFields,
          action: "complete_profile",
        }),
      });
    } catch (error) {
      console.error("Failed to send profile completion notification:", error);
    }
  }

  // Document verification notifications
  static async notifyDocumentVerified(recipientUserId, documentType) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "Document Verified ",
        message: `Your ${documentType} has been successfully verified. You can now access additional features.`,
        type: notificationService.NotificationTypes.SUCCESS,
        metadata: JSON.stringify({
          documentType: documentType,
          verificationDate: new Date().toISOString(),
          action: "explore_features",
        }),
      });
    } catch (error) {
      console.error(
        "Failed to send document verification notification:",
        error
      );
    }
  }

  static async notifyDocumentRejected(recipientUserId, documentType, reason) {
    try {
      await notificationService.createNotification({
        userId: recipientUserId,
        title: "Document Needs Attention",
        message: `Your ${documentType} requires revision: ${reason}`,
        type: notificationService.NotificationTypes.ERROR,
        metadata: JSON.stringify({
          documentType: documentType,
          rejectionReason: reason,
          action: "resubmit_document",
        }),
      });
    } catch (error) {
      console.error("Failed to send document rejection notification:", error);
    }
  }

  // Price alert notifications
  static async notifyPriceAlert(
    recipientUserId,
    productName,
    currentPrice,
    targetPrice
  ) {
    try {
      const isAbove = currentPrice > targetPrice;
      await notificationService.createNotification({
        userId: recipientUserId,
        title: `Price Alert: ${productName}`,
        message: `${productName} is now $${currentPrice}/MT ${
          isAbove ? "above" : "below"
        } your target of $${targetPrice}/MT`,
        type: notificationService.NotificationTypes.INFO,
        metadata: JSON.stringify({
          productName: productName,
          currentPrice: currentPrice,
          targetPrice: targetPrice,
          priceDirection: isAbove ? "above" : "below",
          action: "view_market_prices",
        }),
      });
    } catch (error) {
      console.error("Failed to send price alert notification:", error);
    }
  }

  // Bulk notification utility for admin operations
  static async sendBulkNotification(userIds, notificationData) {
    try {
      const promises = userIds.map((userId) =>
        notificationService.createNotification({
          ...notificationData,
          userId: userId,
        })
      );

      await Promise.allSettled(promises);
      console.log(`Bulk notification sent to ${userIds.length} users`);
    } catch (error) {
      console.error("Failed to send bulk notifications:", error);
    }
  }

  // Test notification for development
  static async sendTestNotification() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await notificationService.createNotification({
        userId: user.attributes.sub,
        title: "Test Notification",
        message:
          "This is a test notification to verify the system is working correctly.",
        type: notificationService.NotificationTypes.INFO,
        metadata: JSON.stringify({
          test: true,
          timestamp: new Date().toISOString(),
        }),
      });
      console.log("Test notification sent successfully");
    } catch (error) {
      console.error("Failed to send test notification:", error);
    }
  }
}

export default NotificationTriggers;
